import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTopFiveBalance, buyGroup, getExternalStoreFee } from '@/utils/services/user.service';
import { fetchAllCurrencies1 } from '@/redux/actions/FetchCoinLists';
import { toast } from 'react-toastify';
import {
  transformVirtualWalletData,
  transformOnchainWalletData,
  transformBankData,
} from '@/store/Layout/dashboard/utils/walletDataTransformers';
import {
  ChevronDown,
  ChevronRight,
  CreditCard,
  Eye,
  EyeOff,
  Landmark,
  Layers,
  RefreshCw,
  ShieldCheck,
  Wallet,
  X,
} from 'lucide-react';
import {
  SidebarOverlay,
  SidebarContainer,
  SidebarPanel,
  NeonGlow,
  SidebarHeader,
  HeaderLeft,
  WalletIconContainer,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  IconButton,
  SidebarBody,
  SectionTitle,
  BalanceCard,
  BalanceCardHeader,
  BalanceCardLeft,
  WalletIconBox,
  PrimaryPill,
  BankModeButton,
  BankDetailBox,
  LongPressBtn,
  Divider,
  SidebarFooter,
  FooterText,
  StepIndicatorRow,
  StepTrack,
  StepDot,
  StepLabel,
  StepContentColumn,
  FormLabel,
  FormInput,
  FormTextarea,
  FormHint,
  PrimaryButton,
  OrderSummaryBox,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  SummaryTotalRow,
  SummaryTotal,
  BalanceCheckSection,
  BalanceMessage,
  CartItemsScroll,
  CartItemCard,
  CartItemName,
  CartItemMeta,
  CartItemPrice,
  EmptyCartMessage,
  SectionHint,
  BackButton,
  FooterActionsRow,
  LoadingBalancesMessage,
} from './styles';

const MOCK_BALANCES = [
  { kind: 'bank', label: 'Bank Wallet', totalFiat: 22310, fiatCcy: 'EUR', tokens: [{ symbol: 'EUR', value: 22310 }, { symbol: 'USD', value: 1450 }] },
  { kind: 'virtual', label: 'Virtual Wallet', totalFiat: 12840.22, fiatCcy: 'EUR', tokens: [{ symbol: 'USDT', value: 4820.5 }, { symbol: 'CTY', value: 315000 }] },
  { kind: 'onchain', label: 'On-chain Wallet', totalFiat: 5940.12, fiatCcy: 'EUR', tokens: [{ symbol: 'BNB', value: 3.42 }, { symbol: 'USDT', value: 1120 }] },
  { kind: 'card', label: 'Card Wallet', totalFiat: 0, fiatCcy: 'USD', tokens: [] },
];

function formatMoney(n, ccy) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'EUR', maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${n?.toLocaleString() ?? 0} ${ccy || 'EUR'}`;
  }
}

// Format amount using display currency (no hardcoded symbol)
function formatAmount(n, displayCurrency) {
  const ccy = displayCurrency || 'USD';
  const validFiat = /^(USD|EUR|GBP|CHF|JPY|CAD|AUD)$/i.test(ccy);
  if (validFiat) {
    return formatMoney(Number(n), ccy);
  }
  return `${Number(n).toFixed(2)} ${ccy}`;
}

function walletIcon(kind) {
  if (kind === 'virtual') return <Layers size={16} />;
  if (kind === 'onchain') return <Wallet size={16} />;
  if (kind === 'card') return <CreditCard size={16} />;
  return <Landmark size={16} />;
}

function LongPressPayButton({ disabled, amount, symbol, holdSeconds = 1, onConfirm, onPay }) {
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [paid, setPaid] = useState(null);
  const [processing, setProcessing] = useState(false);
  const timer = useRef(null);

  const start = () => {
    if (disabled || paid) return;
    setHolding(true);
    const startTs = Date.now();
    timer.current = window.setInterval(() => {
      const pct = Math.min(100, ((Date.now() - startTs) / (holdSeconds * 1000)) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timer.current);
        setHolding(false);
        setProgress(0);
        // Call onPay if provided, otherwise call onConfirm
        if (onPay) {
          setProcessing(true);
          onPay()
            .then(() => {
              const receipt = { amount, token: symbol || 'CARD', network: symbol ? 'Blockchain' : 'Card', timestamp: new Date().toISOString(), hash: Math.random().toString(36).slice(2) };
              setPaid(receipt);
              setProcessing(false);
              onConfirm?.(receipt);
            })
            .catch((error) => {
              setProcessing(false);
              console.error('Payment error:', error);
            });
        } else {
          const receipt = { amount, token: symbol || 'CARD', network: symbol ? 'Blockchain' : 'Card', timestamp: new Date().toISOString(), hash: Math.random().toString(36).slice(2) };
          setPaid(receipt);
          onConfirm?.(receipt);
        }
      }
    }, 50);
  };

  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    setHolding(false);
    setProgress(0);
  };

  if (paid) {
    return (
      <div style={{ borderRadius: 16, border: '1px solid rgba(16, 185, 129, 0.3)', background: 'rgba(16, 185, 129, 0.1)', padding: '8px 12px', fontSize: 12, color: '#6ee7b7' }}>
        <div style={{ fontWeight: 600 }}>Paid ✓</div>
        <div style={{ marginTop: 4 }}>Amount: {paid.amount} • {paid.token}</div>
      </div>
    );
  }

  if (processing) {
    return (
      <div style={{ borderRadius: 16, border: '1px solid rgba(34, 211, 238, 0.3)', background: 'rgba(34, 211, 238, 0.1)', padding: '8px 12px', fontSize: 12, color: '#67e8f9' }}>
        <div style={{ fontWeight: 600 }}>Processing...</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <LongPressBtn
        disabled={disabled}
        onMouseDown={start}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchEnd={stop}
      >
        <span style={{ position: 'relative', zIndex: 2 }}>Pay {amount}</span>
        {!disabled && holding && (
          <span style={{ position: 'absolute', inset: 0, background: 'rgba(34, 211, 238, 0.3)', width: `${progress}%` }} />
        )}
      </LongPressBtn>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Hold {holdSeconds}s to confirm</div>
    </div>
  );
}

function BalanceCardItem({ w, reveal, expanded, onToggle, onToggleReveal, amountToPay, actions, allowedSymbol, onPayWithToken }) {
  const isBank = w.kind === 'bank';
  const isCard = w.kind === 'card';
  const [bankMode, setBankMode] = useState('bonifico');
  const [cardAmount, setCardAmount] = useState(amountToPay ?? 250);
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const payAmount = amountToPay ?? 250;
  const canPay = w.totalFiat >= payAmount && payAmount > 0;
  const payAmountDisplay = Number(payAmount || 0).toFixed(4);
  const cardValid = cardAmount > 0 && cardHolder.length > 3 && cardNumber.length >= 12 && cardExpiry.length >= 4 && cardCvv.length >= 3;
  
  // Helper function to check if a token symbol matches the allowed symbol
  const tokenMatchesSymbol = (tokenSymbol) => {
    if (!allowedSymbol) return false;
    const token = (tokenSymbol || '').toUpperCase();
    const allowed = allowedSymbol.toUpperCase();
    // Extract base symbol (e.g., "USDT" from "USDT (BEP20)")
    const baseTokenSymbol = token.split(' ')[0];
    const baseAllowed = allowed.split(' ')[0];
    return token === allowed || token.includes(allowed) || allowed.includes(token) || baseTokenSymbol === baseAllowed;
  };
  
  // Show all tokens, but only enable pay for matching ones
  const allTokens = w.tokens ?? [];
  
  // Check if wallet has matching tokens (for non-bank and non-card wallets)
  const hasMatchingToken = isBank || isCard || allTokens.some(t => tokenMatchesSymbol(t.symbol));

  return (
    <BalanceCard $expanded={expanded}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <BalanceCardHeader onClick={onToggle} style={{ flex: 1 }}>
          <BalanceCardLeft>
            <WalletIconBox>{walletIcon(w.kind)}</WalletIconBox>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{w.label}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{isCard ? 'Pay with credit / debit card' : 'Total balance'}</div>
            </div>
          </BalanceCardLeft>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{reveal ? formatMoney(w.totalFiat, w.fiatCcy) : '••••••'}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{w.fiatCcy}</div>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
          </div>
        </BalanceCardHeader>
        {(hasMatchingToken || isCard) && (
          <PrimaryPill
            onClick={() => {
              if (w.kind === 'virtual') {
                if (!expanded) onToggle();
                if (canPay) actions?.onSelectPayMethod?.(w.kind);
              } else if (isCard) {
                if (!expanded) onToggle();
                actions?.onSelectPayMethod?.('card');
              } else {
                // For other wallets, just expand/collapse
                onToggle();
              }
            }}
            style={w.kind !== 'virtual' && !isCard ? { opacity: 0.6, cursor: 'pointer' } : {}}
          >
            <CreditCard size={16} /> Pay
          </PrimaryPill>
        )}
      </div>

      {expanded && isBank && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => setBankMode('bonifico')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 12,
                fontSize: 12,
                border: `1px solid ${bankMode === 'bonifico' ? 'rgba(34, 211, 238, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                background: bankMode === 'bonifico' ? 'rgba(34, 211, 238, 0.1)' : 'rgba(255,255,255,0.05)',
                color: bankMode === 'bonifico' ? '#67e8f9' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Bonifico
            </button>
            <button
              type="button"
              onClick={() => setBankMode('riba')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 12,
                fontSize: 12,
                border: `1px solid ${bankMode === 'riba' ? 'rgba(34, 211, 238, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                background: bankMode === 'riba' ? 'rgba(34, 211, 238, 0.1)' : 'rgba(255,255,255,0.05)',
                color: bankMode === 'riba' ? '#67e8f9' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Ri.Ba
            </button>
          </div>

          {bankMode === 'bonifico' && (
            <div style={{
              padding: 12,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.02)',
              fontSize: 12,
            }}>
              <div style={{ marginBottom: 8, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Bonifico Bancario</div>
              <div style={{ marginBottom: 4 }}>IBAN: <span style={{ fontFamily: 'monospace' }}>IT60X0542811101000000123456</span></div>
              <div style={{ marginBottom: 4 }}>BIC / SWIFT: <span style={{ fontFamily: 'monospace' }}>BCITITMM</span></div>
              <div style={{ marginBottom: 4 }}>Beneficiario: Custodiy Financial Services Ltd</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>Causale: Invoice #{Date.now()}</div>
            </div>
          )}

          {bankMode === 'riba' && (
            <div style={{
              padding: 12,
              borderRadius: 12,
              border: '1px solid rgba(34, 211, 238, 0.2)',
              background: 'rgba(6, 182, 212, 0.05)',
              fontSize: 12,
            }}>
              <div style={{ marginBottom: 8, fontWeight: 600, color: '#67e8f9' }}>Ri.Ba – Ricevuta Bancaria</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <input placeholder="Debtor Company Name" style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                <input placeholder="Partita IVA / VAT" style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                <input placeholder="IBAN Debtor" style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                <input type="number" placeholder="Importo" style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                <input placeholder="Data Scadenza" style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                <input placeholder="Causale" style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
              </div>
              <div style={{ marginTop: 12, padding: 8, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                • Generazione distinta CBI XML<br />
                • Firma digitale aziendale<br />
                • Invio banca via CBI / Open Banking<br />
                • Gestione esito (Accettata / Insoluta)
              </div>
            </div>
          )}
        </div>
      )}

      {expanded && isCard && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <input
              type="number"
              value={cardAmount}
              onChange={(e) => setCardAmount(Number(e.target.value))}
              placeholder="Amount"
              style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '10px 12px', fontSize: 13, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
            />
            <input
              value={cardHolder}
              onChange={(e) => setCardHolder(e.target.value)}
              placeholder="Card holder"
              style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '10px 12px', fontSize: 13, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
            />
            <input
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="Card number"
              style={{ width: '100%', borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '10px 12px', fontSize: 13, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input
                value={cardExpiry}
                onChange={(e) => setCardExpiry(e.target.value)}
                placeholder="MM/YY"
                style={{ borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '10px 12px', fontSize: 13, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
              />
              <input
                value={cardCvv}
                onChange={(e) => setCardCvv(e.target.value)}
                placeholder="CVV"
                style={{ borderRadius: 12, background: 'rgba(255,255,255,0.1)', padding: '10px 12px', fontSize: 13, border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
              />
            </div>
            <LongPressPayButton
              disabled={!cardValid}
              amount={cardAmount}
              symbol=""
              onConfirm={() => {
                actions?.onSelectPayMethod?.('card');
                alert('Card payment confirmed');
              }}
            />
          </div>
        </div>
      )}

      {expanded && !isBank && !isCard && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>Token breakdown</div>
            <IconButton title={reveal ? 'Hide' : 'Reveal'} onClick={onToggleReveal}>{reveal ? <EyeOff size={16} /> : <Eye size={16} />}</IconButton>
          </div>
          {allTokens.length === 0 ? (
            <div style={{ marginTop: 8, padding: '12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              No tokens available
            </div>
          ) : (
            <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
              {allTokens.slice(0, 6).map((t) => {
                const matchesSymbol = tokenMatchesSymbol(t.symbol);
                return (
                  <div key={t.symbol} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
                    <div>
                      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{t.symbol}</div>
                      {payAmount > 0 && matchesSymbol && w.kind === 'virtual' && (
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                          To pay: {payAmountDisplay} {t.symbol}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{reveal ? t.value?.toLocaleString() : '••••'}</div>
                      {matchesSymbol && w.kind === 'virtual' && (
                        <LongPressPayButton 
                          disabled={!canPay} 
                          amount={payAmountDisplay} 
                          symbol={t.symbol} 
                          onPay={() => onPayWithToken?.(t.symbol)}
                          onConfirm={() => actions?.onSelectPayMethod?.(w.kind)} 
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </BalanceCard>
  );
}

export function PaymentSidebar({
  open,
  onOpenChange,
  balances: balancesProp,
  amountToPay,
  productSymbol,
  groupId,
  orderDetail,
  deliveryInfo,
  products, // Array of products with id, name, title, price, image/pictures for overview step
  shippingCost , // Shipping cost (from cart/order, not hardcoded)
  displayCurrency = 'USD', // Currency for displaying amounts (e.g. 'USD', 'EUR')
  title = 'Wallet Summary',
  subtitle = 'Virtual Wallet',
  actions,
  onPurchaseSuccess, // Callback to refetch group details after successful purchase
  categoryId, // Category ID for fetching sales tax
  // Bid mode (Place Bid flow – same 3 steps, pay via onBidPay instead of buyGroup)
  mode = 'group', // 'group' | 'bid' | 'buy'
  bidQuantity,
  onBidQuantityChange,
  bidTotalAmount, // When mode='bid', use this as total (includes auction fee etc.)
  onBidPay, // (deliveryInfo, symbol) => Promise<void>
  // Buy mode (Buy Now – same 3 steps, pay via onBuyPay instead of buyGroup)
  onBuyPay, // (deliveryInfo: { deliveryCountry, deliveryAddress, secondDeliveryAddress, phoneNumber }, symbol: string) => Promise<void>
}) {
  console.log("🚀 ~ PaymentSidebar ~ shippingCost:", shippingCost)
  console.log("🚀 ~ PaymentSidebar ~ orderDetail:", orderDetail)
  const dispatch = useDispatch();
  const { coinList: virtualWalletCoins } = useSelector((state) => ({
    coinList: state.coinList1?.data || [],
  }));
  const [walletData, setWalletData] = useState(null);
  const [bankData, setBankData] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [salesTaxFee, setSalesTaxFee] = useState(0);
  const [salesTaxSymbol, setSalesTaxSymbol] = useState(null); // Symbol from sales tax API response
  const [reveal, setReveal] = useState(false);
  const [payMethod, setPayMethod] = useState(null);
  const [expanded, setExpanded] = useState({ virtual: true, onchain: false, bank: false });
  const [orderBreakdownExpanded, setOrderBreakdownExpanded] = useState(false);
  const panelRef = useRef(null);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  // Step 1 form fields
  const [deliveryCountry, setDeliveryCountry] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [secondDeliveryAddress, setSecondDeliveryAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Sales tax state - similar to ProductInvoice.jsx
  const [taxedArray, setTaxedArray] = useState([]); // Array of { id: categoryId, val: fee }
  const [taxedArray1, setTaxedArray1] = useState([]); // Array of { id: categoryId, per: percentage }
  // Default to 'Amount' mode (can be changed to 'Percentage' if needed)
  const selectedTaxMode = 'Amount'; // 'Amount' or 'Percentage'
  const [totalPerSum, setTotalPerSum] = useState(0); // Sum for percentage mode

  // Fetch sales tax fee for a specific category (similar to ProductInvoice.jsx)
  const fetchSalesTaxFeeForCategory = useCallback(async (catId) => {
    if (!catId) return { fee: 0, percentage: 0, symbol: null };
    try {
      const requestData = {
        type: "sale_tax",

      };
      const response = await getExternalStoreFee(requestData);
      if (response.data) {
        const fee = response?.data?.data?.fee || 0;
        const percentage = response?.data?.data?.percentage || 0;
        const symbol = response?.data?.data?.symbol || null;
        return { fee, percentage, symbol };
      } else {
        console.error("getExternalStoreFee:", response.statusText);
        return { fee: 0, percentage: 0, symbol: null };
      }
    } catch (error) {
      console.error("Error fetching sales tax fee:", error);
      return { fee: 0, percentage: 0, symbol: null };
    }
  }, []);

  // Fetch sales tax for all product categories
  const fetchAllSalesTaxFees = useCallback(async () => {
    if (!orderDetail || !Array.isArray(orderDetail) || orderDetail.length === 0) {
      setSalesTaxFee(0);
      return;
    }

    // Get unique category IDs from products
    const categoryIds = [];
    orderDetail.forEach((item) => {
      const product = products?.find((p) => p.id === item.product_id || p._id === item.product_id);
      const catId = product?.category_id || categoryId;
      if (catId && !categoryIds.includes(catId)) {
        categoryIds.push(catId);
      }
    });

    // If no category IDs found, use the provided categoryId
    if (categoryIds.length === 0 && categoryId) {
      categoryIds.push(categoryId);
    }

    if (categoryIds.length === 0) {
      setSalesTaxFee(0);
      return;
    }

    try {
      // Fetch tax for each category
      const taxPromises = categoryIds.map((catId) => fetchSalesTaxFeeForCategory(catId));
      const taxResults = await Promise.all(taxPromises);

      // Build taxed arrays similar to ProductInvoice.jsx
      const feeArray = categoryIds.map((catId, index) => ({
        id: catId,
        val: taxResults[index].fee || 0,
      }));
      const percentageArray = categoryIds.map((catId, index) => ({
        id: catId,
        per: taxResults[index].percentage || 0,
      }));

      setTaxedArray(feeArray);
      setTaxedArray1(percentageArray);

      // Store symbol from first result (assuming all categories have same symbol)
      if (taxResults.length > 0 && taxResults[0].symbol) {
        setSalesTaxSymbol(taxResults[0].symbol);
      }

      // Calculate sales tax: if fee exists use fee, otherwise use percentage of product price
      let totalSalesTax = 0;
      
      // First, check if any category has a fee > 0
      const hasFee = feeArray.some((item) => item.val > 0);
      
      if (hasFee) {
        // Use fee mode: sum all fee values
        totalSalesTax = feeArray.reduce((sum, item) => sum + item.val, 0);
      } else {
        // Use percentage mode: calculate tax per product and sum
        orderDetail.forEach((item) => {
          const product = products?.find((p) => p.id === item.product_id || p._id === item.product_id);
          const catId = product?.category_id || categoryId;
          const taxInfo = percentageArray.find((t) => t.id === catId);
          if (taxInfo && taxInfo.per > 0) {
            const productTotal = (item.quantity || 0) * (item.price || 0);
            const productTax = (taxInfo.per / 100) * productTotal;
            totalSalesTax += productTax;
          }
        });
      }
      
      setSalesTaxFee(totalSalesTax);
      setTotalPerSum(totalSalesTax); // Store for potential future use
    } catch (error) {
      console.error("Error fetching sales tax fees:", error);
      setSalesTaxFee(0);
    }
  }, [orderDetail, products, categoryId, fetchSalesTaxFeeForCategory]);

  useEffect(() => {
    if (!open || balancesProp) return;
    const fetchBalances = async () => {
      setBalancesLoading(true);
      try {
        dispatch(fetchAllCurrencies1()).catch(() => {});
        const balanceRes = await getTopFiveBalance();
        setWalletData(balanceRes?.data);
        if (balanceRes?.data?.bank) setBankData(balanceRes.data.bank);
        else if (balanceRes?.data?.striga) setBankData(balanceRes.data.striga);
      } catch (err) {
        console.error('Error fetching wallet balances:', err);
      } finally {
        setBalancesLoading(false);
      }
    };
    fetchBalances();
  }, [open, balancesProp, dispatch]);

  // Fetch sales tax when sidebar opens and orderDetail/products are available
  useEffect(() => {
    if (open && orderDetail && orderDetail.length > 0) {
      fetchAllSalesTaxFees();
    } else if (open && categoryId) {
      // Fallback to single category if no orderDetail yet
      fetchSalesTaxFeeForCategory(categoryId).then((result) => {
        // Calculate sales tax: if fee exists use fee, otherwise use percentage
        let calculatedTax = 0;
        if (result.fee > 0) {
          calculatedTax = result.fee;
        } else if (result.percentage > 0 && orderDetail && orderDetail.length > 0) {
          // Calculate percentage of product prices
          orderDetail.forEach((item) => {
            const productTotal = (item.quantity || 0) * (item.price || 0);
            calculatedTax += (result.percentage / 100) * productTotal;
          });
        }
        setSalesTaxFee(calculatedTax);
        setSalesTaxSymbol(result.symbol);
        setTaxedArray([{ id: categoryId, val: result.fee }]);
        setTaxedArray1([{ id: categoryId, per: result.percentage }]);
      });
    }
  }, [open, orderDetail, categoryId, fetchAllSalesTaxFees, fetchSalesTaxFeeForCategory]);
  

  const dynamicBalances = useMemo(() => {
    if (balancesProp) return balancesProp;
    const transformVirtual = transformVirtualWalletData(virtualWalletCoins);
    const virtualTotal = (() => {
      const stored = localStorage.getItem('internalCoins');
      if (stored) return parseFloat(String(stored).replace(/"/g, '')) || 0;
      return transformVirtual.reduce((acc, a) => acc + a.balance, 0);
    })();
    const onchainRaw = walletData?.onchain?.[0]?.topBalances || [];
    const onchainMapped = onchainRaw.map((item) => ({
      symbol: item.coin_type || item.symbol,
      quantity: parseFloat(item.balance || 0),
      coin_price: parseFloat(item.coin_price || item.price || 0),
    }));
    const transformOnchain = transformOnchainWalletData(onchainMapped);
    const onchainTotal = transformOnchain.reduce((acc, a) => acc + a.balance, 0);
    const bankItems = transformBankData(bankData);
    const bankTotal = bankData?.totalBalance
      ? parseFloat(bankData.totalBalance) || 0
      : bankItems.reduce((acc, a) => acc + a.balance, 0);
    return [
      { kind: 'bank', label: 'Bank Wallet', totalFiat: bankTotal, fiatCcy: 'USD', tokens: bankItems.map((i) => ({ symbol: i.currency, value: i.qty })) },
      { kind: 'virtual', label: 'Virtual Wallet', totalFiat: virtualTotal, fiatCcy: 'USD', tokens: transformVirtual.map((i) => ({ symbol: i.symbol, value: i.qty })) },
      { kind: 'onchain', label: 'On-chain Wallet', totalFiat: onchainTotal, fiatCcy: 'USD', tokens: transformOnchain.map((i) => ({ symbol: i.symbol, value: i.qty })) },
      { kind: 'card', label: 'Card Wallet', totalFiat: 0, fiatCcy: 'USD', tokens: [] },
    ];
  }, [balancesProp, virtualWalletCoins, walletData, bankData]);

  const balances = balancesProp ?? (balancesLoading && !walletData ? MOCK_BALANCES : dynamicBalances);

  // Calculate total available balance across all wallets
  const totalAvailableBalance = useMemo(() => {
    if (!balances || !Array.isArray(balances)) return 0;
    return balances.reduce((sum, wallet) => {
      // Convert to USD if needed (simplified - you may need currency conversion)
      return sum + (wallet.totalFiat || 0);
    }, 0);
  }, [balances]);

  // Calculate order summary for overview step
  const orderSummary = useMemo(() => {
    if (!orderDetail || !Array.isArray(orderDetail)) {
      return { items: [], subtotal: 0, shipping: shippingCost, salesTax: 0, vat: 0, total: shippingCost };
    }

    const items = orderDetail.map((item) => {
      console.log("🚀 ~ PaymentSidebar ~ item:", item)
      const product = products?.find((p) => p.id === item.product_id || p._id === item.product_id);
      const productName = product?.name || product?.title || item.title || `Product ${item.product_id}`;
      const productImage = product?.pictures?.[0] || product?.image || product?.product_image || product?.coverImage || null;
      const quantity = item.quantity || 0;
      const price = item.price || 0;
      const productTotal = quantity * price;
      
      // Calculate VAT: (vat% / 100) * product_price * quantity
      const vatPercentage = item.vat || product?.vat || 0;
      const vatAmount = (vatPercentage / 100) * productTotal;

      return {
        product_id: item.product_id,
        name: productName,
        image: productImage,
        quantity,
        price,
        total: productTotal,
        vat: vatAmount,
      };
    });

    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const salesTax = salesTaxFee || 0;
    const vat = items.reduce((sum, item) => sum + item.vat, 0); // Sum of all VAT amounts
    let total = subtotal + shippingCost + salesTax + vat;
    if (mode === 'bid' && bidTotalAmount != null && typeof bidTotalAmount === 'number') {
      total = bidTotalAmount;
    }

    return { items, subtotal, shipping: shippingCost, salesTax, vat, total };
  }, [orderDetail, products, shippingCost, salesTaxFee, mode, bidTotalAmount]);
  console.log("🚀 ~ PaymentSidebar ~ orderSummary:", orderSummary)

  // Check if user has sufficient balance
  const hasSufficientBalance = totalAvailableBalance >= orderSummary.total;
  const balanceShortage = orderSummary.total - totalAvailableBalance;

  useEffect(() => {
    const fn = (e) => { if (open && e.key === 'Escape') onOpenChange(false); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset step and form when sidebar closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
      setDeliveryCountry('');
      setDeliveryAddress('');
      setSecondDeliveryAddress('');
      setPhoneNumber('');
      setPayMethod(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => panelRef.current?.focus?.(), 10);
    return () => clearTimeout(t);
  }, [open]);

  const payMethodLabel = payMethod === 'virtual' ? 'Virtual Wallet' : payMethod === 'onchain' ? 'On-chain Wallet' : payMethod === 'bank' ? 'Bank Wallet' : payMethod === 'card' ? 'Card Wallet' : '';

  const handleRefresh = async () => {
    if (balancesProp) return actions?.onRefresh?.();
    setBalancesLoading(true);
    try {
      dispatch(fetchAllCurrencies1()).catch(() => {});
      const balanceRes = await getTopFiveBalance();
      setWalletData(balanceRes?.data);
      if (balanceRes?.data?.bank) setBankData(balanceRes.data.bank);
      else if (balanceRes?.data?.striga) setBankData(balanceRes.data.striga);
    } catch (err) {
      console.error('Error refreshing balances:', err);
    } finally {
      setBalancesLoading(false);
    }
  };

  const handlePayWithToken = async (symbol) => {
    // Use delivery info from step 1 (internal state) or from props
    const deliveryData = deliveryInfo || {
      deliveryCountry,
      deliveryAddress,
      secondDeliveryAddress,
      phoneNumber,
    };

    if (!deliveryData.deliveryCountry || !deliveryData.deliveryAddress || !deliveryData.phoneNumber) {
      toast.error('Please complete delivery information first');
      return Promise.reject(new Error('Missing delivery information'));
    }

    const totalAmount = orderSummary.total;
    if (totalAvailableBalance < totalAmount) {
      const shortage = totalAmount - totalAvailableBalance;
      toast.error(`Insufficient balance. You need ${formatAmount(shortage, displayCurrency)} more to complete this purchase.`);
      return Promise.reject(new Error('Insufficient balance'));
    }

    // Bid mode: call onBidPay instead of buyGroup
    if (mode === 'bid' && typeof onBidPay === 'function') {
      try {
        await onBidPay(
          {
            deliveryCountry: deliveryData.deliveryCountry,
            deliveryAddress: deliveryData.deliveryAddress,
            secondDeliveryAddress: deliveryData.secondDeliveryAddress || '',
            phoneNumber: deliveryData.phoneNumber,
          },
          symbol
        );
        toast.success('Bid submitted successfully!');
        if (onOpenChange) onOpenChange(false);
        if (onPurchaseSuccess && typeof onPurchaseSuccess === 'function') {
          try {
            await onPurchaseSuccess();
          } catch (e) {
            console.error('onPurchaseSuccess after bid:', e);
          }
        }
        return;
      } catch (error) {
        const msg = error?.response?.data?.Message || error?.response?.data?.message || error?.message || 'Bid failed';
        toast.error(msg);
        throw error;
      }
    }

    // Buy mode (Buy Now): call onBuyPay instead of buyGroup
    if (mode === 'buy' && typeof onBuyPay === 'function') {
      try {
        await onBuyPay(
          {
            deliveryCountry: deliveryData.deliveryCountry,
            deliveryAddress: deliveryData.deliveryAddress,
            secondDeliveryAddress: deliveryData.secondDeliveryAddress || '',
            phoneNumber: deliveryData.phoneNumber,
          },
          symbol,
          { totalAmount: orderSummary.total, saleTax: orderSummary.salesTax }
        );
        toast.success('Payment successful!');
        if (onOpenChange) onOpenChange(false);
        if (onPurchaseSuccess && typeof onPurchaseSuccess === 'function') {
          try {
            await onPurchaseSuccess();
          } catch (e) {
            console.error('onPurchaseSuccess after buy:', e);
          }
        }
        return;
      } catch (error) {
        const msg = error?.response?.data?.Message || error?.response?.data?.message || error?.message || 'Payment failed';
        toast.error(msg);
        throw error;
      }
    }

    if (!groupId || !orderDetail) {
      toast.error('Missing required information for payment');
      return Promise.reject(new Error('Missing required information'));
    }

    // Add delivery_charges to each item in order_detail
    const orderDetailWithDeliveryCharges = orderDetail.map((item) => ({
      ...item,
      delivery_charges: shippingCost,
    }));

    const payload = {
      total_amount: totalAmount, // Includes subtotal + shipping charges + sales tax + VAT
      delivery_country: deliveryData.deliveryCountry,
      delivery_address: deliveryData.deliveryAddress,
      second_delivery_address: deliveryData.secondDeliveryAddress || '',
      phone_number: deliveryData.phoneNumber,
      group_id: groupId,
      symbol: symbol,
      order_detail: orderDetailWithDeliveryCharges,
    };

    try {
      const response = await buyGroup(payload);
      toast.success('Payment successful!');
      
      // Refresh balances after successful purchase (virtual wallet and on-chain wallet)
      try {
        setBalancesLoading(true);
        await dispatch(fetchAllCurrencies1());
        const balanceRes = await getTopFiveBalance();
        setWalletData(balanceRes?.data);
        if (balanceRes?.data?.bank) setBankData(balanceRes.data.bank);
        else if (balanceRes?.data?.striga) setBankData(balanceRes.data.striga);
      } catch (refreshError) {
        console.error('Error refreshing balances after purchase:', refreshError);
        // Don't throw - purchase was successful, just log the refresh error
      } finally {
        setBalancesLoading(false);
      }
      
      // Refetch group details to update data (order_data, aggregated_volume, audit timeline, etc.)
      if (onPurchaseSuccess && typeof onPurchaseSuccess === 'function') {
        try {
          await onPurchaseSuccess();
        } catch (refetchError) {
          console.error('Error refetching group details after purchase:', refetchError);
          // Don't throw - purchase was successful, just log the refetch error
        }
      }
      
      // Close the sidebar after successful purchase
      if (onOpenChange) {
        onOpenChange(false);
      }
      
      return response;
    } catch (error) {
      const errorMessage = error?.response?.data?.Message || error?.response?.data?.message || error?.message || 'Payment failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const defaultActions = {
    onSelectPayMethod: (kind) => setPayMethod(kind),
    onRefresh: handleRefresh,
    onGoFullWallet: () => actions?.onGoFullWallet?.(),
    onOpenRisk: () => actions?.onOpenRisk?.(),
    ...actions,
  };

  const sidebarContent = (
    <>
      {open && <SidebarOverlay onClick={() => onOpenChange(false)} />}
      <SidebarContainer $open={open} aria-hidden={!open}>
        <SidebarPanel ref={panelRef} tabIndex={-1}>
          <NeonGlow />
          <SidebarHeader>
            <HeaderLeft>
              <WalletIconContainer><Wallet size={20} style={{ color: 'rgba(255,255,255,0.9)' }} /></WalletIconContainer>
              <div>
                <HeaderTitle>{title}</HeaderTitle>
                <HeaderSubtitle>{subtitle}</HeaderSubtitle>
              </div>
            </HeaderLeft>
            <HeaderActions>
              <IconButton title="Refresh" onClick={defaultActions.onRefresh}><RefreshCw size={16} /></IconButton>
              <IconButton title={reveal ? 'Hide' : 'Reveal'} onClick={() => setReveal((s) => !s)}>{reveal ? <EyeOff size={16} /> : <Eye size={16} />}</IconButton>
              <IconButton title="Close" onClick={() => onOpenChange(false)}><X size={16} /></IconButton>
            </HeaderActions>
          </SidebarHeader>

          <SidebarBody>
            {/* Step indicator - 3 steps */}
            <StepIndicatorRow>
              <StepTrack $active={currentStep >= 1}>
                <StepDot $active={currentStep >= 1}>1</StepDot>
              </StepTrack>
              <StepLabel>{currentStep === 1 ? 'Information' : currentStep === 2 ? 'Overview' : 'Transaction'}</StepLabel>
              <StepTrack $active={currentStep >= 2}>
                <StepDot $active={currentStep >= 2}>2</StepDot>
              </StepTrack>
              <StepLabel>{currentStep === 2 ? 'Overview' : currentStep === 3 ? 'Transaction' : ''}</StepLabel>
              <StepTrack $active={currentStep >= 3}>
                <StepDot $active={currentStep >= 3}>3</StepDot>
              </StepTrack>
            </StepIndicatorRow>

            {currentStep === 1 ? (
              /* Step 1: Information */
              <StepContentColumn>
                {mode === 'bid' && (
                  <>
                    <SectionTitle>BID DETAILS</SectionTitle>
                    <div>
                      <FormLabel>Quantity *</FormLabel>
                      <FormInput
                        type="number"
                        min={1}
                        value={bidQuantity ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            onBidQuantityChange?.(null);
                            return;
                          }
                          const n = parseInt(v, 10);
                          if (!isNaN(n) && n >= 1) onBidQuantityChange?.(n);
                        }}
                        placeholder="Quantity"
                      />
                    </div>
                  </>
                )}
                <SectionTitle>DELIVERY INFORMATION</SectionTitle>
                <FormHint>Please provide your delivery details</FormHint>

                <div>
                  <FormLabel>Delivery Country *</FormLabel>
                  <FormInput
                    type="text"
                    value={deliveryCountry}
                    onChange={(e) => setDeliveryCountry(e.target.value)}
                    placeholder="Enter country"
                  />
                </div>

                <div>
                  <FormLabel>Delivery Address *</FormLabel>
                  <FormTextarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Enter delivery address"
                    rows={3}
                  />
                </div>

                <div>
                  <FormLabel>Second Delivery Address</FormLabel>
                  <FormTextarea
                    value={secondDeliveryAddress}
                    onChange={(e) => setSecondDeliveryAddress(e.target.value)}
                    placeholder="Enter second delivery address (optional)"
                    rows={3}
                  />
                </div>

                <div>
                  <FormLabel>Phone Number *</FormLabel>
                  <FormInput
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = value.replace(/[^0-9+\-() ]/g, '');
                      setPhoneNumber(numericValue);
                    }}
                    onKeyPress={(e) => {
                      const char = String.fromCharCode(e.which);
                      if (!/[0-9+\-() ]/.test(char)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter phone number"
                  />
                </div>

                <div style={{ marginTop: 8 }}>
                  <PrimaryButton
                    onClick={() => {
                      const bidOk = mode !== 'bid' || (bidQuantity != null && Number(bidQuantity) >= 1);
                      if (deliveryCountry.trim() && deliveryAddress.trim() && phoneNumber.trim() && bidOk) {
                        setCurrentStep(2);
                      }
                    }}
                    disabled={
                      !deliveryCountry.trim() ||
                      !deliveryAddress.trim() ||
                      !phoneNumber.trim() ||
                      (mode === 'bid' && (bidQuantity == null || Number(bidQuantity) < 1))
                    }
                  >
                    Continue to Overview
                  </PrimaryButton>
                </div>
              </StepContentColumn>
            ) : currentStep === 2 ? (
              /* Step 2: Overview */
              <StepContentColumn>
                <SectionTitle>ORDER OVERVIEW</SectionTitle>

                <CartItemsScroll>
                  {orderSummary.items.length === 0 ? (
                    <EmptyCartMessage>No items in cart</EmptyCartMessage>
                  ) : (
                    orderSummary.items.map((item, index) => (
                      <CartItemCard key={item.product_id || index}>
                        {item.image ? (
                          <img
                            src={item.image}
                            alt=""
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: 'cover',
                              borderRadius: 8,
                              flexShrink: 0,
                              background: 'rgba(255,255,255,0.05)',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              flexShrink: 0,
                              background: 'rgba(255,255,255,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'rgba(255,255,255,0.3)',
                              fontSize: 20,
                            }}
                          >
                            •
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <CartItemName>{item.name}</CartItemName>
                          <CartItemMeta>{formatAmount(item.price, displayCurrency)} × {item.quantity}</CartItemMeta>
                        </div>
                        <CartItemPrice>{formatAmount(item.total, displayCurrency)}</CartItemPrice>
                      </CartItemCard>
                    ))
                  )}
                </CartItemsScroll>

                <OrderSummaryBox>
                  <SummaryRow>
                    <SummaryLabel>Subtotal:</SummaryLabel>
                    <SummaryValue>{formatAmount(orderSummary.subtotal, displayCurrency)}</SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryLabel>Shipping:</SummaryLabel>
                    <SummaryValue>{formatAmount(orderSummary.shipping, displayCurrency)}</SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryLabel>Sales Tax:</SummaryLabel>
                    <SummaryValue>{formatAmount(orderSummary.salesTax, displayCurrency)}{salesTaxSymbol && <span style={{ marginLeft: 4, fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>({salesTaxSymbol})</span>}</SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryLabel>VAT:</SummaryLabel>
                    <SummaryValue>{formatAmount(orderSummary.vat, displayCurrency)}</SummaryValue>
                  </SummaryRow>
                  <SummaryTotalRow>
                    <SummaryTotal>Total:</SummaryTotal>
                    <SummaryTotal>{formatAmount(orderSummary.total, displayCurrency)}</SummaryTotal>
                  </SummaryTotalRow>
                  <BalanceCheckSection>
                    <SummaryRow>
                      <SummaryLabel>Available Balance:</SummaryLabel>
                      <SummaryValue style={{ color: hasSufficientBalance ? '#6ee7b7' : '#fb7185' }}>{formatAmount(totalAvailableBalance, displayCurrency)}</SummaryValue>
                    </SummaryRow>
                    {!hasSufficientBalance && (
                      <BalanceMessage $error>⚠️ Insufficient balance. You need {formatAmount(balanceShortage, displayCurrency)} more to complete this purchase.</BalanceMessage>
                    )}
                    {hasSufficientBalance && (
                      <BalanceMessage $success>✓ Sufficient balance available</BalanceMessage>
                    )}
                  </BalanceCheckSection>
                </OrderSummaryBox>

                <SectionHint>Cart activity (qty over time)</SectionHint>
                <div style={{
                  height: 100,
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: 12,
                }}>
                  No activity data
                </div>

                <div style={{ marginTop: 8 }}>
                  <PrimaryButton
                    onClick={() => {
                      if (!hasSufficientBalance) {
                        toast.error(`Insufficient balance. You need ${formatAmount(balanceShortage, displayCurrency)} more.`);
                        return;
                      }
                      setCurrentStep(3);
                    }}
                    disabled={!hasSufficientBalance}
                  >
                    {hasSufficientBalance ? 'Continue to Payment' : 'Insufficient Balance'}
                  </PrimaryButton>
                </div>
              </StepContentColumn>
            ) : (
              /* Step 3: Make Transaction */
              <>
                <SectionTitle>BALANCES</SectionTitle>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4, marginBottom: 12 }}>Tap a card to expand • Pay selects method</div>
                
                {/* Order Summary - Collapsible */}
                {(() => {
                  const totalAmount = orderSummary.total;
                  const hasEnoughBalance = totalAvailableBalance >= totalAmount;
                  return (
                    <div style={{
                      marginBottom: 16,
                      borderRadius: 12,
                      background: hasEnoughBalance 
                        ? 'rgba(16, 185, 129, 0.1)' 
                        : 'rgba(251, 113, 133, 0.1)',
                      border: `1px solid ${hasEnoughBalance 
                        ? 'rgba(16, 185, 129, 0.3)' 
                        : 'rgba(251, 113, 133, 0.3)'}`,
                    }}>
                      {/* Header - Always visible */}
                      <div 
                        onClick={() => setOrderBreakdownExpanded(!orderBreakdownExpanded)}
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>Total Required:</span>
                          <span style={{ fontSize: 15, fontWeight: 600, color: '#67e8f9' }}>
                            {formatAmount(totalAmount, displayCurrency)}
                          </span>
                        </div>
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          style={{ 
                            color: 'rgba(255,255,255,0.5)',
                            transition: 'transform 0.2s',
                            transform: orderBreakdownExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                      
                      {/* Expandable Breakdown */}
                      {orderBreakdownExpanded && (
                        <div style={{ padding: '0 16px 12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{ paddingTop: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Subtotal:</span>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                                {formatAmount(orderSummary.subtotal, displayCurrency)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Shipping:</span>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                                {formatAmount(orderSummary.shipping, displayCurrency)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Sales Tax:</span>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                                {formatAmount(orderSummary.salesTax, displayCurrency)}
                                {salesTaxSymbol && <span style={{ marginLeft: 4, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>({salesTaxSymbol})</span>}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>VAT:</span>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                                {formatAmount(orderSummary.vat, displayCurrency)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Available Balance */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px 16px',
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                      }}>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Available Balance:</span>
                        <span style={{ 
                          fontSize: 14, 
                          fontWeight: 600, 
                          color: hasEnoughBalance ? '#6ee7b7' : '#fb7185' 
                        }}>
                          {formatAmount(totalAvailableBalance, displayCurrency)}
                        </span>
                      </div>
                      
                      {/* Status Message */}
                      <div style={{
                        margin: '0 16px 12px 16px',
                        padding: '8px 12px',
                        borderRadius: 8,
                        background: hasEnoughBalance ? 'rgba(16, 185, 129, 0.15)' : 'rgba(251, 113, 133, 0.15)',
                        color: hasEnoughBalance ? '#6ee7b7' : '#fb7185',
                        fontSize: 12,
                      }}>
                        {hasEnoughBalance 
                          ? '✓ Sufficient balance available' 
                          : `⚠️ Insufficient balance. You need ${formatAmount(totalAmount - totalAvailableBalance, displayCurrency)} more.`}
                      </div>
                    </div>
                  );
                })()}

                {balancesLoading && !walletData ? (
                  <LoadingBalancesMessage>Loading balances...</LoadingBalancesMessage>
                ) : (
              balances.map((w) => (
                <BalanceCardItem
                  key={w.kind}
                  w={w}
                  reveal={reveal}
                  expanded={expanded[w.kind]}
                  onToggle={() => setExpanded({ virtual: w.kind === 'virtual', onchain: w.kind === 'onchain', bank: w.kind === 'bank', card: w.kind === 'card' })}
                  onToggleReveal={() => setReveal((s) => !s)}
                  amountToPay={orderSummary.total}
                  actions={defaultActions}
                  allowedSymbol={productSymbol}
                  onPayWithToken={handlePayWithToken}
                />
              ))
                )}
              </>
            )}
          </SidebarBody>

          <SidebarFooter>
            <Divider />
            {currentStep === 2 && (
              <FooterActionsRow>
                <BackButton onClick={() => setCurrentStep(1)}>← Back</BackButton>
                <div style={{ flex: 1 }} />
                <IconButton title="Close" onClick={() => onOpenChange(false)}><X size={16} /></IconButton>
              </FooterActionsRow>
            )}
            {currentStep === 3 && (
              <FooterActionsRow>
                <BackButton onClick={() => setCurrentStep(2)}>← Back</BackButton>
                <div style={{ flex: 1 }} />
                <PrimaryPill onClick={defaultActions.onGoFullWallet}><Wallet size={16} /> Full Wallet</PrimaryPill>
                <PrimaryPill onClick={defaultActions.onOpenRisk}><ShieldCheck size={16} /> Risk</PrimaryPill>
                <IconButton title="Close" onClick={() => onOpenChange(false)}><X size={16} /></IconButton>
              </FooterActionsRow>
            )}
            {currentStep === 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                <IconButton title="Close" onClick={() => onOpenChange(false)}><X size={16} /></IconButton>
              </div>
            )}
            {currentStep === 3 && payMethod && <div style={{ marginTop: 8, fontSize: 11, color: '#67e8f9' }}>Payment method: {payMethodLabel}</div>}
            <FooterText>Custodiy • Wallet Sidebar PRO</FooterText>
          </SidebarFooter>
        </SidebarPanel>
      </SidebarContainer>
    </>
  );

  if (!open) return null;
  return ReactDOM.createPortal(sidebarContent, document.body);
}

export default PaymentSidebar;
